<script setup>
import CFTicketModalInner from '#modules/support/components/Ticket/CFTicketModalInner.vue';
import CFButton from '@/ui/Button/CFButton.vue';
import { useTicketStore } from '#modules/support/stores/ticket.store.js';
import CFFormInput from '@/shared/components/CFFormInput.vue';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import CFFormTextarea from '@/shared/components/CFFormTextarea.vue';
import CFDropzone from '@/shared/components/CFDropzone.vue';
import CFTicketAttachment from '#modules/support/components/Ticket/CFTicketAttachment.vue';
import CFDropzoneOverlay from '@/shared/components/CFDropzoneOverlay.vue';
import CFCircleBackButton from '@/shared/components/CFCircleBackButton.vue';
import CFAlert from '@/ui/Alert/CFAlert.vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import { ALLOWED_ATTACHMENT_EXTENSIONS, ATTACHMENT_ACCEPT } from '@/modules/support/constants/attachments.js';

const { t } = useI18n();
const ticketStore = useTicketStore();

const fileInput = ref();
const form = reactive({
    title: '',
    message: '',
    department: 'support',
    attachments: [],
});

const createTicket = async () => {
    let ticket = { ...form };

    if (ticketStore.state.domain?.id) {
        ticket.product_type = 'domain';
        ticket.product_id = ticketStore.state.domain.id;
    }

    if (ticketStore.state.ssl?.id) {
        ticket.product_type = 'ssl-certificate';
        ticket.product_id = ticketStore.state.ssl.id;
    }

    if (ticketStore.state.request_id) {
        ticket.message += `\n\n(Request ID ${ticketStore.state.request_id})`;
    }

    if (ticketStore.state.context) {
        const contextLines = Object.entries(ticketStore.state.context)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
        ticket.message += `\n\nContext:\n${contextLines}`;
    }

    await ticketStore.create(ticket);
};

const addAttachment = (files) => {
    const file = files[0];

    if (!file) {
        return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();

    if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
        useToast().error(t('support.attachment_extension_not_supported', { ext }));
        fileInput.value.value = '';
        return;
    }

    form.attachments.push(file);
    fileInput.value.value = '';
};

const removeAttachment = (index) => {
    form.attachments.splice(index, 1);
};

const relatedDomain = computed(() => {
    return ticketStore.state.domain
        ? ticketStore.state.domain.attributes.name + '.' + ticketStore.state.domain.attributes.tld
        : null;
});

const relatedSSL = computed(() => {
    return ticketStore.state.ssl ? ticketStore.state.ssl.attributes.subject_name : null;
});
</script>

<template>
    <CFTicketModalInner :title="$t('support.create_ticket_title')">
        <form
            method="post"
            @submit.prevent="createTicket"
        >
            <div class="flex flex-col gap-4">
                <CFAlert v-if="ticketStore.state.request_id">
                    {{ $t('support.request_is_attached') }}
                </CFAlert>

                <CFFormInput
                    v-if="relatedDomain"
                    :model-value="relatedDomain"
                    :label="$t('support.related_product')"
                    position="inset"
                    readonly
                />

                <CFFormInput
                    v-if="relatedSSL"
                    :model-value="relatedSSL"
                    :label="$t('support.related_product')"
                    position="inset"
                    readonly
                />

                <CFFormInput
                    v-model="form.title"
                    :label="$t('support.title_label')"
                    :placeholder="$t('support.title_placeholder')"
                    position="inset"
                    :loading="ticketStore.isLoading"
                    :errors="ticketStore.errors.hasType('title') || ticketStore.errors.hasData('title')"
                />

                <CFFormTextarea
                    v-model="form.message"
                    :label="$t('support.message_label')"
                    :placeholder="$t('support.message_placeholder')"
                    :loading="ticketStore.isLoading"
                    :invalid="ticketStore.errors.hasType('message') || ticketStore.errors.hasData('message')"
                />
            </div>

            <h3 class="mt-6 text-base font-bold">
                {{ $t('support.add_file') }}
            </h3>
            <p class="mt-2 mb-4 text-sm">
                {{ $t('support.add_file_description', { extensions: ATTACHMENT_ACCEPT }) }}
            </p>

            <CFDropzone
                v-if="form.attachments.length < 5"
                v-slot="{ active }"
                class="bg-accent-200/20 hover:bg-accent-300/30 relative mb-4 cursor-pointer p-4 transition duration-150 ease-in"
                @files-dropped="addAttachment"
                @click="fileInput.click()"
            >
                <CFDropzoneOverlay :visible="active" />

                <input
                    ref="fileInput"
                    type="file"
                    class="hidden"
                    :accept="ATTACHMENT_ACCEPT"
                    @change="addAttachment($event.target.files)"
                />

                <div
                    class="border-accent text-accent flex flex-col items-center justify-center gap-4 border border-dashed p-6 text-sm"
                >
                    <CFIcon
                        name="download-thick-bottom"
                        size="80"
                    />
                    <div class="flex gap-1">
                        <span>{{ $t('support.drop_files_here_or') }}</span>
                        <span class="lowercase underline underline-offset-3">
                            {{ $t('button.select_file') }}
                        </span>
                    </div>
                </div>
            </CFDropzone>

            <div
                v-if="form.attachments.length > 0"
                class="divide-neutrals-400 border-neutrals-400 divide-y border-y"
            >
                <CFTicketAttachment
                    v-for="(attachment, index) in form.attachments"
                    :key="index"
                    :file="attachment"
                    @remove="removeAttachment(index)"
                />
            </div>
        </form>

        <template #actions>
            <CFCircleBackButton @click="ticketStore.goToPrevious()">
                {{ $t('button.back') }}
            </CFCircleBackButton>

            <CFButton
                type="button"
                :loading="ticketStore.isLoading"
                @click="createTicket"
            >
                {{ $t('button.ask_question') }}
            </CFButton>
        </template>
    </CFTicketModalInner>
</template>
